import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { useLocation } from "react-router-dom";
import { makeApi, URLS } from "Utils/apiURL";
import deepResearchStore from "Stores/deepResearchData";
import ConversationStarters from "./components/ConversationStarters";
import SellingSignals from "./components/SellingSignals";
import ProfileSummary from "./components/ProfileSummary";
import LeadershipProfile from "./components/LeadershipProfile";
import KeyMentions from "./components/KeyMentions";
import CompanyOverview from "./components/CompanyOverview";
import SellerProfileBar from "./components/SellerProfileBar";
import SellerProfileSetup from "./components/SellerProfileSetup";
import "./deepResearch.scss";

const LOADING_MESSAGES = [
  { text: "🔎 Searching the web for {{name}}'s public profiles…" },
  { text: "📇 Mapping social handles and digital footprint..." },
  { text: "🎯 Highlighting major milestones, recognitions, and achievements..." },
  { text: "🧠 Analyzing {{name}}'s LinkedIn to decode thought leadership and content patterns..." },
  { text: "📚 Reading through {{name}}'s career journey, company transitions, and leadership roles..." },
  { text: "🎙️ Looking for mentions in podcasts, events, and interviews..." },
  { text: "🖇️ Scanning for exclusive community memberships and networks..." },
  { text: "💼 Reviewing {{name}}'s company's market position, product focus, and traction..." },
  { text: "🌐 Searching for recent mentions in news, blogs, and startup forums..." },
  { text: "📊 Generating high-value talking points and insights for your outreach..." },
  { text: "✅ Almost there… finalizing your personalized deep research summary!" }
];

// ── Left Section: Progress panel with animated loading messages ──
const LeftSection = observer(({ loadingList, contactName, isComplete, timer }) => {
  const lastRef = useCallback((el) => {
    if (el) el.scrollIntoView({ behavior: "smooth" });
  }, [loadingList.length]);

  return (
    <div className="drLeftPanel">
      <div className="drLeftProfile">
        <div className="drLeftName">{contactName}</div>
      </div>
      <div className="drLeftProgress">
        {!isComplete && (
          <div className="drLeftHeader">
            <span className="drLeftDots">●●●</span>
            <div className="drLeftTitleWrap">
              <p className="drLeftTitle">Deep Research</p>
              <span className="drLeftTimer">{timer.toFixed(1)} seconds</span>
            </div>
          </div>
        )}
        {isComplete && (
          <div className="drLeftCompleted">
            <span className="drLeftCompletedIcon">✓</span>
            <p>Deep Research Completed</p>
            <span className="drLeftCompletedTime"><b>{timer.toFixed(1)}</b> seconds</span>
          </div>
        )}
        <div className="drLeftList">
          <div className="drLeftListInner">
            {loadingList.map((item, index) => {
              const isLast = index === loadingList.length - 1;
              return (
                <div className="drLeftListItem" key={index} ref={isLast ? lastRef : null}>
                  <span className={`drLeftItemIcon ${isLast && !isComplete ? "spinner" : "tick"}`}>
                    {isLast && !isComplete ? "⟳" : "✓"}
                  </span>
                  <p>{item}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
});

// ── Chat Panel: replaces LeftSection once data is available ──
const ChatPanel = observer(({ contactName, contactId }) => {
  // Hydrate from the store's chatHistory (rehydrated by setProfileResult on overview load)
  const conversations = deepResearchStore.chatHistory;
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [previousId, setPreviousId] = useState("");
  const endRef = useRef(null);

  const quickActions = useMemo(() => {
    const who = contactName || "this prospect";
    return [
      { label: "Cold Email", icon: "📧", query: `Write a personalized cold email to ${who}` },
      { label: "LinkedIn DM", icon: "💬", query: `Write a LinkedIn message to ${who}` },
      { label: "Cold Call Script", icon: "📞", query: `Write a cold call script for ${who}` },
      { label: "Follow-up Email", icon: "📨", query: `Write a follow-up email to ${who}` }
    ];
  }, [contactName]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
  }, [conversations.length, sending]);

  const send = (query) => {
    if (!query || !query.trim() || sending) return;

    const userMsg = { role: "user", content: query, ts: Date.now() };
    deepResearchStore.appendChatMessage(userMsg);
    const nextHistory = [...conversations, userMsg];
    setInput("");
    setSending(true);

    const profile = deepResearchStore.profileData || {};
    makeApi({
      url: URLS.deepResearchUserProfile.replace("/userProfile", "/getResponseFromAI"),
      method: "POST",
      data: {
        query_type: "general",
        query,
        contactId: contactId || "",
        // Rich grounding context — the backend prompt asks the model to cite from these.
        prospectInsights: deepResearchStore.structuredProfile || {},
        companyInsights: deepResearchStore.companyInsights || {},
        sellingSignals: deepResearchStore.sellingSignals || [],
        conversationStarters: deepResearchStore.conversationStarters || [],
        keyMentions: deepResearchStore.insightsData?.key_mentions || [],
        companyData: deepResearchStore.companyData || {},
        deepDiveIntel: deepResearchStore.deepDiveIntel || {},
        sellerProfile: deepResearchStore.activeSellerProfile || {},
        profileData: {
          firstName: profile.firstName,
          lastName: profile.lastName,
          subTitle: profile.subTitle,
          company: profile?.position?.[0]?.company?.name
        },
        previousId: previousId || "",
        previousChats: nextHistory.map(c => ({
          type: c.role === "user" ? "user" : "ai",
          content: c.content,
          ...(c.subject ? { subject: c.subject, body: c.body } : {})
        }))
      }
    }).then(res => {
      const data = res?.data;
      if (data) {
        const aiMsg = {
          role: "ai",
          type: data.type || "text",
          content: data.text || data.body || "",
          subject: data.subject || "",
          body: data.body || "",
          ts: Date.now()
        };
        if (data.response_id) setPreviousId(data.response_id);
        deepResearchStore.appendChatMessage(aiMsg);
      }
      setSending(false);
    }).catch(() => setSending(false));
  };

  return (
    <div className="drChatPanel">
      <div className="drChatHeader">
        <span className="drChatTitle">Research Assistant</span>
        <span className="drChatHint">Chat about {contactName || "this prospect"}</span>
      </div>
      {conversations.length === 0 && (
        <div className="drChatQuickActions">
          {quickActions.map((a, i) => (
            <button key={i} className="drChatQuickAction" onClick={() => send(a.query)}>
              <span>{a.icon}</span> <span>{a.label}</span>
            </button>
          ))}
        </div>
      )}
      <div className="drChatMessages">
        {conversations.map((msg, i) => (
          <div key={i} className={`drChatMsg drChatMsg-${msg.role}`}>
            <div className="drChatMsgAvatar">{msg.role === "user" ? "👤" : "🤖"}</div>
            <div className="drChatMsgContent">
              {msg.type === "email" ? (
                <div className="drChatEmail">
                  {msg.subject && <div className="drChatEmailSubject">Subject: {msg.subject}</div>}
                  <div className="drChatEmailBody">{msg.body || msg.content}</div>
                </div>
              ) : (
                <div className="drChatText">{msg.content}</div>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="drChatMsg drChatMsg-ai">
            <div className="drChatMsgAvatar">🤖</div>
            <div className="drChatMsgContent">
              <div className="drChatTyping"><span>.</span><span>.</span><span>.</span></div>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      <div className="drChatInput">
        <input
          type="text"
          placeholder="Ask anything..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") send(input); }}
          disabled={sending}
        />
        <button onClick={() => send(input)} disabled={sending || !input.trim()}>
          Send
        </button>
      </div>
    </div>
  );
});

// ── Right Section: SERP links + quick insights + full details ──
const getDomainFromUrl = (url) => {
  try {
    return new URL(url).hostname.replace("www.", "");
  } catch {
    return "";
  }
};

const RenderLinks = observer(({ links, insights, searchText, isLoading, onComplete }) => {
  const [visibleCount, setVisibleCount] = useState(0);
  const [showExpanded, setShowExpanded] = useState(false);
  const [animationDone, setAnimationDone] = useState(false);
  const maxCollapsed = 7;

  // Animate links appearing one by one
  useEffect(() => {
    if (links.length === 0) return;
    setVisibleCount(0);
    setAnimationDone(false);
    let i = 0;
    const interval = setInterval(() => {
      i++;
      if (i <= Math.min(links.length, maxCollapsed)) {
        setVisibleCount(i);
      } else {
        clearInterval(interval);
        setAnimationDone(true);
        if (onComplete) onComplete();
      }
    }, 150);
    return () => clearInterval(interval);
  }, [links.length]);

  const displayLinks = showExpanded ? links : links.slice(0, visibleCount);

  const scrollRef = useCallback((el) => {
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <div className={`drLinksWrapper ${!isLoading && insights.length > 0 ? "loaded" : ""}`}>
      <div className="drSearchHeader">
        <p className="drSearchTitle" ref={scrollRef}>
          🔎 {searchText}
          {isLoading && <span className="drSearchDots"><span>.</span><span>.</span><span>.</span></span>}
        </p>
      </div>
      {displayLinks.length > 0 && (
        <div className="drLinksList">
          {displayLinks.map((item, index) => {
            const domain = getDomainFromUrl(item.link || "");
            const faviconUrl = domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=32` : "";
            return (
              <div className="drLinkItem drLinkAnimate" key={index} style={{ animationDelay: `${index * 0.05}s` }}>
                <a href={item.link} target="_blank" rel="noopener noreferrer" className="drLinkAnchor">
                  {faviconUrl ? (
                    <img src={faviconUrl} alt="" className="drLinkFaviconImg" />
                  ) : (
                    <span className="drLinkFavicon">?</span>
                  )}
                  <span className="drLinkTitle">{item.title}</span>
                </a>
                {showExpanded && item.snippet && (
                  <p className="drLinkSnippet">{item.snippet}</p>
                )}
              </div>
            );
          })}
          {animationDone && links.length > maxCollapsed && !showExpanded && (
            <div className="drLinksFooter">
              <button className="drSeeMore" onClick={() => { setShowExpanded(true); setVisibleCount(links.length); }}>
                See more
              </button>
              <span className="drLinksCount">{links.length} web pages</span>
            </div>
          )}
          {showExpanded && (
            <div className="drLinksFooter">
              <button className="drSeeMore" onClick={() => { setShowExpanded(false); setVisibleCount(maxCollapsed); }}>
                See less
              </button>
            </div>
          )}
        </div>
      )}
      {insights.length > 0 && (
        <div className="drQuickInsights">
          <div className="drInsightsTitle">Quick Insights</div>
          {insights.map((item, index) => (
            <p className="drInsightItem" key={index}>
              {typeof item === "string" ? item : item.insight || ""}
            </p>
          ))}
        </div>
      )}
    </div>
  );
});

const DeepResearch = observer(({ contactData = {} }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location?.search || "");

  const linkedInUrl = contactData?.linkedInUrl || contactData?.linkedin_url
    || queryParams.get("linkedInUrl") || deepResearchStore.currentLinkedInUrl || "";
  const contactName = contactData?.firstName
    ? `${contactData.firstName} ${contactData.lastName || ""}`.trim()
    : queryParams.get("contactName") || deepResearchStore.currentContactName || "";

  const [showSellerPopup, setShowSellerPopup] = useState(false);
  const [loadingList, setLoadingList] = useState([]);
  const [timer, setTimer] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [researchStarted, setResearchStarted] = useState(false);
  const [overviewChecked, setOverviewChecked] = useState(false);
  const [userSearchLinks, setUserSearchLinks] = useState([]);
  const [userSearchInsights, setUserSearchInsights] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [companySearchLinks, setCompanySearchLinks] = useState([]);
  const [companySearchInsights, setCompanySearchInsights] = useState([]);
  const [companySearchLoading, setCompanySearchLoading] = useState(false);
  const [companySearchStarted, setCompanySearchStarted] = useState(false);

  // Normalize URL
  const normalizedUrl = useMemo(() => {
    let url = linkedInUrl;
    if (!url.startsWith("http")) url = "https://" + url;
    if (!url.includes("linkedin.com")) url = "https://www.linkedin.com/in/" + url;
    return url;
  }, [linkedInUrl]);

  const msgInterval = 4000;

  useEffect(() => {
    deepResearchStore.fetchSellerProfiles();
  }, []);

  // Timer
  useEffect(() => {
    if (!timerActive) return;
    const interval = setInterval(() => setTimer(prev => prev + 0.1), 100);
    return () => clearInterval(interval);
  }, [timerActive]);

  // Loading messages
  useEffect(() => {
    if (!researchStarted) return;
    const interval = setInterval(() => {
      setLoadingList(prev => {
        if (prev.length < LOADING_MESSAGES.length) {
          const msg = LOADING_MESSAGES[prev.length].text.replace(/\{\{name\}\}/g, contactName || "prospect");
          return [...prev, msg];
        }
        clearInterval(interval);
        return prev;
      });
    }, msgInterval);
    return () => clearInterval(interval);
  }, [researchStarted, contactName]);

  // Start research
  const startResearch = useCallback(() => {
    if (!linkedInUrl) return;
    setResearchStarted(true);
    setTimerActive(true);
    deepResearchStore.reset();
    deepResearchStore.setLoading(true, "researching");

    // Build search query from contact name (not LinkedIn URL)
    const searchName = contactName || "prospect";
    const searchTitle = contactData?.jobTitle || contactData?.title || "";
    const searchCompany = contactData?.company || "";
    const personSearchQuery = [searchName, searchTitle, searchCompany].filter(Boolean).join(", ");

    // 1. User search — fires immediately, shows links in ~3s while userProfile runs
    setUserSearchLoading(true);
    makeApi({
      url: URLS.deepResearchUserSearch,
      method: "POST",
      data: { searchQuery: personSearchQuery }
    }).then(res => {
      const data = res?.data;
      if (data && !data.error) {
        setUserSearchLinks(data.links?.organic || []);
        const ins = data.insights;
        setUserSearchInsights(Array.isArray(ins) ? ins : (ins?.insights || []));
      }
      setUserSearchLoading(false);

      // 2. Company search — fires after person search completes
      if (searchCompany) {
        setCompanySearchStarted(true);
        setCompanySearchLoading(true);
        makeApi({
          url: URLS.deepResearchUserSearch,
          method: "POST",
          data: { searchQuery: `${searchCompany} recent news updates` }
        }).then(res2 => {
          const data2 = res2?.data;
          if (data2 && !data2.error) {
            setCompanySearchLinks(data2.links?.organic || []);
            const ins2 = data2.insights;
            setCompanySearchInsights(Array.isArray(ins2) ? ins2 : (ins2?.insights || []));
          }
          setCompanySearchLoading(false);
        }).catch(() => setCompanySearchLoading(false));
      }
    }).catch(() => setUserSearchLoading(false));

    // 3. Full profile — fires in parallel, takes ~40s but SERP links show within 3-5s above
    const activeProfile = deepResearchStore.activeSellerProfile;
    makeApi({
      url: URLS.deepResearchUserProfile,
      method: "POST",
      data: {
        linked_in_url: normalizedUrl,
        contactId: contactData?.contactId || "",
        seller_profile: activeProfile || {},
        seller_profile_id: activeProfile?.id || "",
        model: deepResearchStore.researchMode || "instant"
      }
    }).then(response => {
      if (response?.data) {
        deepResearchStore.setProfileResult(response.data);
        deepResearchStore.setLoading(false, "complete");
      } else {
        deepResearchStore.setError("No data returned");
      }
      setTimerActive(false);
    }).catch(err => {
      deepResearchStore.setError(err?.message || "Research failed");
      setTimerActive(false);
    });
  }, [linkedInUrl, normalizedUrl, contactName]);

  // Reset and re-fetch whenever the contact changes (e.g. switching contacts
  // in the slider without unmounting the component).
  useEffect(() => {
    const cId = contactData?.contactId;
    if (!cId) {
      setOverviewChecked(true);
      return;
    }

    // Wipe state from any previously-loaded contact
    deepResearchStore.reset();
    setResearchStarted(false);
    setOverviewChecked(false);
    setLoadingList([]);
    setTimer(0);
    setTimerActive(false);
    setUserSearchLinks([]);
    setUserSearchInsights([]);
    setUserSearchLoading(false);
    setCompanySearchLinks([]);
    setCompanySearchInsights([]);
    setCompanySearchLoading(false);
    setCompanySearchStarted(false);

    let cancelled = false;
    makeApi({
      url: `${URLS.deepResearchOverview}/${cId}`,
      method: "GET"
    }).then(res => {
      if (cancelled) return;
      if (res?.data && Object.keys(res.data).length > 0) {
        deepResearchStore.setProfileResult(res.data);
        deepResearchStore.setLoading(false, "complete");
        setResearchStarted(true);
      }
      setOverviewChecked(true);
    }).catch(() => {
      if (!cancelled) setOverviewChecked(true);
    });

    return () => { cancelled = true; };
  }, [contactData?.contactId]);

  // Gate auto-start on overview-check + seller profiles loaded.
  // - If existing data was loaded from DB (hasData), do nothing — legacy rows
  //   without a seller_profile_id intentionally leave the dropdown unselected.
  // - If no seller profiles exist yet, force the setup popup to create one.
  // - Otherwise, auto-pick the first profile (for new research) and start.
  useEffect(() => {
    if (!overviewChecked) return;
    if (!deepResearchStore.sellerProfilesLoaded) return;
    if (!linkedInUrl) return;
    if (researchStarted || deepResearchStore.hasData) return;

    if (!deepResearchStore.sellerProfiles.length) {
      setShowSellerPopup(true);
      return;
    }
    if (!deepResearchStore.activeSellerProfileId) {
      // For *new* research with no profile picked yet, default to the first one.
      deepResearchStore.setActiveSellerProfileId(deepResearchStore.sellerProfiles[0].id);
      return;
    }
    startResearch();
  }, [overviewChecked, deepResearchStore.sellerProfilesLoaded, deepResearchStore.sellerProfiles.length, deepResearchStore.activeSellerProfileId, linkedInUrl, researchStarted]);

  const { isLoading, loadingPhase, hasData } = deepResearchStore;
  const isComplete = loadingPhase === "complete";

  // ── Initial loading state — wait until we know whether existing research exists ──
  if (!overviewChecked || !deepResearchStore.sellerProfilesLoaded) {
    return (
      <div className="deepResearchContainer">
        <div className="drInitialLoading">
          <div className="drInitialLoadingSpinner" />
          <p>Loading deep research…</p>
        </div>
      </div>
    );
  }

  // ── Landing state ──
  if (!researchStarted && !hasData) {
    return (
      <div className="deepResearchContainer">
        {showSellerPopup && (
          <SellerProfileSetup
            existingProfile={deepResearchStore.activeSellerProfile}
            onClose={() => setShowSellerPopup(false)}
            onSave={(profile) => {
              const editingId = deepResearchStore.activeSellerProfileId;
              const payload = editingId ? { ...profile, id: editingId } : profile;
              deepResearchStore.saveSellerProfile(payload);
              setShowSellerPopup(false);
            }}
          />
        )}
        <SellerProfileBar
          profiles={deepResearchStore.sellerProfiles}
          activeProfileId={deepResearchStore.activeSellerProfileId}
          loadedResearchProfileId={deepResearchStore.loadedResearchSellerProfileId}
          mode={deepResearchStore.researchMode}
          onModeChange={(m) => deepResearchStore.setResearchMode(m)}
          onSelectProfile={(id) => deepResearchStore.setActiveSellerProfileId(id)}
          onAddNewProfile={() => {
            deepResearchStore.setActiveSellerProfileId(null);
            setShowSellerPopup(true);
          }}
          onEditProfile={() => setShowSellerPopup(true)}
        />
        <div className="drLanding">
          <span style={{ fontSize: "48px" }}>🔬</span>
          <h2 className="drLandingTitle">Deep Research</h2>
          {contactName && (
            <p className="drLandingSubtitle">
              Discover what makes <strong>{contactName}</strong> tick
            </p>
          )}
          <div className="drLandingFeatures">
            <div className="drFeatureItem"><span>💬</span><span>Conversation starters that actually get replies</span></div>
            <div className="drFeatureItem"><span>📈</span><span>Selling signals with urgency scoring</span></div>
            <div className="drFeatureItem"><span>🏢</span><span>Company intel — competitors, tech stack, hiring</span></div>
            <div className="drFeatureItem"><span>⏰</span><span>Timing signals — why reach out right now</span></div>
          </div>
          <button
            className="drStartBtn"
            onClick={() => {
              if (!linkedInUrl) return;
              if (!deepResearchStore.sellerProfiles.length) {
                setShowSellerPopup(true);
                return;
              }
              if (!deepResearchStore.activeSellerProfileId) {
                // pick first by default if none selected
                deepResearchStore.setActiveSellerProfileId(deepResearchStore.sellerProfiles[0].id);
              }
              startResearch();
            }}
            disabled={!linkedInUrl}
          >
            🔬 {linkedInUrl ? "Start Deep Research" : "LinkedIn URL required"}
          </button>
        </div>
      </div>
    );
  }

  // ── Research state: two-panel layout ──
  return (
    <div className="deepResearchContainer">
      {showSellerPopup && (
        <SellerProfileSetup
          existingProfile={deepResearchStore.activeSellerProfile}
          onClose={() => setShowSellerPopup(false)}
          onSave={(profile) => {
            const editingId = deepResearchStore.activeSellerProfileId;
            const payload = editingId ? { ...profile, id: editingId } : profile;
            deepResearchStore.saveSellerProfile(payload);
            setShowSellerPopup(false);
          }}
        />
      )}
      <SellerProfileBar
        profiles={deepResearchStore.sellerProfiles}
        activeProfileId={deepResearchStore.activeSellerProfileId}
        loadedResearchProfileId={deepResearchStore.loadedResearchSellerProfileId}
        mode={deepResearchStore.researchMode}
        onModeChange={(m) => deepResearchStore.setResearchMode(m)}
        onSelectProfile={(id) => deepResearchStore.setActiveSellerProfileId(id)}
        onAddNewProfile={() => {
          deepResearchStore.setActiveSellerProfileId(null);
          setShowSellerPopup(true);
        }}
        onEditProfile={() => setShowSellerPopup(true)}
      />
      <div className="drTwoPanelInner">
      {/* Left Panel — Progress while researching, AI chat once data is loaded */}
      {hasData ? (
        <ChatPanel contactName={contactName} contactId={contactData?.contactId} />
      ) : (
        <LeftSection
          loadingList={loadingList}
          contactName={contactName}
          isComplete={isComplete}
          timer={timer}
        />
      )}

      {/* Right Panel — SERP links + details */}
      <div className="drRightPanel">
        <div className="drRightHeader">
          <span style={{fontSize: "16px"}}>🔬</span>
          <span className="drRightTitle">Deep Research</span>
          <span className="drRightSubtitle">{contactName}</span>
          {isComplete && <span className="drTimeBadge">Done in {timer.toFixed(1)}s</span>}
          {hasData &&
           !timerActive &&
           deepResearchStore.activeSellerProfileId &&
           deepResearchStore.loadedResearchSellerProfileId &&
           deepResearchStore.activeSellerProfileId !== deepResearchStore.loadedResearchSellerProfileId && (
            <button
              className="drRerunBtn"
              onClick={() => {
                deepResearchStore.reset();
                setResearchStarted(false);
                setLoadingList([]);
                setTimer(0);
                setUserSearchLinks([]);
                setUserSearchInsights([]);
                setCompanySearchLinks([]);
                setCompanySearchInsights([]);
                setCompanySearchStarted(false);
                // Effect re-runs research with new active profile via useEffect once researchStarted=false + hasData=false
                startResearch();
              }}
            >
              🔄 Re-run with {(() => {
                const p = deepResearchStore.activeSellerProfile;
                return p?.name || p?.company_name || "this profile";
              })()}
            </button>
          )}
        </div>
        {/* User Search Links */}
        {(userSearchLoading || userSearchLinks.length > 0) && (
          <RenderLinks
            links={userSearchLinks}
            insights={userSearchInsights}
            searchText={`Looking for the latest updates on ${contactName}${contactData?.jobTitle ? `, ${contactData.jobTitle}` : ""}${contactData?.company ? ` at ${contactData.company}` : ""}`}
            isLoading={userSearchLoading}
          />
        )}

        {/* Company Search Links */}
        {companySearchStarted && (companySearchLoading || companySearchLinks.length > 0) && (
          <RenderLinks
            links={companySearchLinks}
            insights={companySearchInsights}
            searchText={`Now, looking up recent updates on ${contactData?.company || "the company"}`}
            isLoading={companySearchLoading}
          />
        )}

        {/* Loading indicator while userProfile is still running */}
        {isLoading && !hasData && (
          <div className="drFetchingIndicator">
            <span className="drFetchingDots"><span>.</span><span>.</span><span>.</span></span>
            <span>Generating personalized insights</span>
          </div>
        )}

        {/* Full Details */}
        {hasData && (
          <div className="drResults">
            {/* 1. Profile Summary */}
            <ProfileSummary
              profileData={deepResearchStore.profileData}
              structuredProfile={deepResearchStore.structuredProfile}
              dataQuality={deepResearchStore.dataQuality}
            />

            {/* 2. Leadership Profile */}
            <LeadershipProfile
              aiInsights={deepResearchStore.profileData?.ai_insights}
              languages={deepResearchStore.profileData?.languages}
            />

            {/* 3. Selling Signals */}
            {deepResearchStore.sellingSignals.length > 0 && (
              <SellingSignals signals={deepResearchStore.sellingSignals} />
            )}

            {/* 4. Conversation Starters */}
            {deepResearchStore.conversationStarters.length > 0 && (
              <ConversationStarters starters={deepResearchStore.conversationStarters} />
            )}

            {/* 5. Key Mentions */}
            <KeyMentions
              mentions={deepResearchStore.insightsData?.key_mentions}
            />

            {/* 6. Company Overview + Insights */}
            <CompanyOverview
              companyData={deepResearchStore.companyData}
              companyInsights={deepResearchStore.companyInsights}
              deepDiveIntel={deepResearchStore.deepDiveIntel}
            />
          </div>
        )}
      </div>
      </div>
    </div>
  );
});

export default DeepResearch;
